"use client";

import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GA4EventAttrType } from "@packages/lib/types/datalayerGA";
import { GAConstants } from "@packages/shared-components/services/constants";
import { LeadFormPageType } from "./types";

/**
 * GA Tracking utility for Lead Forms (Request Info, Get Prospectus Digital/Physical).
 *
 * Implements the Whatuni site tracking requirements from Confluence:
 * "Lead Forms - Technical RQs"
 *
 * Event Types:
 * - page_view: fired when a form page/step loads
 * - track_event: fired on user interactions (clicks)
 * - track_kpi: fired on auth-related actions (registration, login)
 * - revenue_kpi: fired on form submission (request_info_internal, email_submit, prospectus_request)
 *
 * Key Parameters:
 * - form_type: where the user started the form (place_of_action) OR "digital|physical" for prospectus
 * - field: "page" (lead forms are always full-page)
 * - page_section: place_of_action (source page like "search_results", "institution_profile")
 * - submission_type: "google|apple|email|request_info_form|get_prospectus_form|opendays_form"
 * - click_text: "one_tap|normal_prospectus" (for prospectus forms)
 */

export type LeadFormSubmissionType =
  | "google"
  | "apple"
  | "email"
  | "request_info_form"
  | "get_prospectus_form"
  | "opendays_form";

export interface LeadFormGAContext {
  /** Source page / place of action (e.g., "search_results", "institution_profile") */
  pageSection: string;
  /** Form type for prospectus: "digital" | "physical". For request info: "request_info" */
  formType: string;
  /** Always "page" for lead forms */
  field: "page";
  /** How user submitted: request_info_form, get_prospectus_form, etc. */
  submissionType: LeadFormSubmissionType;
  /** For prospectus: "one_tap" | "normal_prospectus" */
  clickText?: string;
  /** Provider type: "advertiser" | "non_advertiser" based on subOrderItemId */
  providerType?: string;
}

// ─── Session Storage Key ───────────────────────────────────────────────────────
const LEAD_FORM_GA_KEY = "lead-form-ga-context";

/**
 * Build and store GA context for lead forms.
 * Called when the lead form page mounts.
 */
export const setLeadFormGAContext = (ctx: Omit<LeadFormGAContext, "field">): void => {
  if (typeof sessionStorage !== "undefined") {
    const fullCtx: LeadFormGAContext = { ...ctx, field: "page" };
    sessionStorage.setItem(LEAD_FORM_GA_KEY, JSON.stringify(fullCtx));
  }
};

/**
 * Get the current lead form GA context from sessionStorage.
 */
export const getLeadFormGAContext = (): LeadFormGAContext => {
  const defaults: LeadFormGAContext = {
    pageSection: "na",
    formType: "na",
    field: "page",
    submissionType: "email",
  };
  if (typeof sessionStorage === "undefined") return defaults;
  try {
    const stored = sessionStorage.getItem(LEAD_FORM_GA_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return defaults;
};

/**
 * Derive GA context from page type and referrer info.
 */
export const deriveLeadFormGAContext = (
  pageType: LeadFormPageType,
  referrerPath?: string,
): Omit<LeadFormGAContext, "field"> => {
  // Determine form_type
  let formType = "request_info";
  let submissionType: LeadFormSubmissionType = "request_info_form";
  if (pageType === "digitalprospectus") {
    formType = "digital";
    submissionType = "get_prospectus_form";
  } else if (pageType === "physicalprospectus") {
    formType = "physical";
    submissionType = "get_prospectus_form";
  } else if (pageType === "opendays") {
    formType = "opendays";
    submissionType = "opendays_form";
  }

  // Determine page_section from referrer
  let pageSection = "na";
  if (referrerPath) {
    if (referrerPath.includes("/search") || referrerPath.includes("/courses"))
      pageSection = "search_results";
    else if (referrerPath.includes("/profiles/") || referrerPath.includes("/university/"))
      pageSection = "institution_profile";
    else if (referrerPath.includes("/course/"))
      pageSection = "course_profile";
    else if (referrerPath.includes("/prospectus"))
      pageSection = "prospectus_listing";
    else
      pageSection = "other";
  }

  return { pageSection, formType, submissionType };
};

// ─── Helper ────────────────────────────────────────────────────────────────────

const buildBasePayload = (ctx: LeadFormGAContext, pageName?: string): Partial<GA4EventAttrType> => ({
  form_type: ctx.formType || "na",
  field: ctx.field || "page",
  page_section: ctx.pageSection || "na",
  submission_type: ctx.submissionType || "na",
  provider_type: ctx.providerType || "na",
  ...(pageName ? { page_name: pageName } : {}),
  ...(ctx.clickText ? { click_text: ctx.clickText } : {}),
});

// ─── Page View Events ──────────────────────────────────────────────────────────

/** Fires when Request Info form page loads */
export const trackRequestInfoPageView = (ctx: LeadFormGAContext, institutionId?: string, institutionName?: string, courseName?: string) => {
  GA4DataLayer({
    event: GAConstants.page_view,
    event_name: GAConstants.page_view,
    page_name: "request_info_start_form",
    ...buildBasePayload(ctx, "request_info_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
  } as GA4EventAttrType);
};

/** Fires when Get Prospectus form page loads (digital or physical) */
export const trackProspectusPageView = (ctx: LeadFormGAContext, institutionId?: string, institutionName?: string, courseName?: string) => {
  GA4DataLayer({
    event: GAConstants.page_view,
    event_name: GAConstants.page_view,
    page_name: "prospectus_start_form",
    ...buildBasePayload(ctx, "prospectus_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
  } as GA4EventAttrType);
};

// ─── Track Event (User Interactions) ───────────────────────────────────────────

/** Fires when user clicks Continue with Google CTA on lead form */
export const trackLeadFormContinueWithGoogle = (ctx: LeadFormGAContext) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "continue_with_google",
    ...buildBasePayload(ctx),
  } as GA4EventAttrType);
};

/** Fires when user clicks Continue with Apple CTA on lead form */
export const trackLeadFormContinueWithApple = (ctx: LeadFormGAContext) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "continue_with_apple",
    ...buildBasePayload(ctx),
  } as GA4EventAttrType);
};

/** Fires when user clicks Sign in hyperlink */
export const trackSigninHyperlinkClick = (ctx: LeadFormGAContext) => {
  GA4DataLayer({
    event: GAConstants.track_event,
    event_name: "signin_hyperlink_click",
    ...buildBasePayload(ctx),
  } as GA4EventAttrType);
};

// ─── Track KPI (Auth Actions) ──────────────────────────────────────────────────

/** Fires when social login + first time user (registration request) */
export const trackLeadFormRegistrationRequest = (ctx: LeadFormGAContext, submissionType: LeadFormSubmissionType) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "registration_request",
    ...buildBasePayload({ ...ctx, submissionType }),
  } as GA4EventAttrType);
};

/** Fires when social login + first time user completes (registration submit) */
export const trackLeadFormRegistrationSubmit = (ctx: LeadFormGAContext, submissionType: LeadFormSubmissionType) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "registration_submit",
    ...buildBasePayload({ ...ctx, submissionType }),
  } as GA4EventAttrType);
};

/** Fires when social login + existing user (login request) */
export const trackLeadFormLoginRequest = (ctx: LeadFormGAContext, submissionType: LeadFormSubmissionType) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "login_request",
    ...buildBasePayload({ ...ctx, submissionType }),
  } as GA4EventAttrType);
};

/** Fires when social login + existing user completes (login submit) */
export const trackLeadFormLoginSubmit = (ctx: LeadFormGAContext, submissionType: LeadFormSubmissionType) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "login_submit",
    ...buildBasePayload({ ...ctx, submissionType }),
  } as GA4EventAttrType);
};

// ─── Revenue KPI (Form Submission) ─────────────────────────────────────────────

/** Fires when Request Info form is submitted successfully */
export const trackRequestInfoSubmit = (ctx: LeadFormGAContext, institutionId?: string, institutionName?: string, courseName?: string, yearOfEntry?: string, marketingConsent?: string) => {
  // request_info_internal
  GA4DataLayer({
    event: GAConstants.revenue_kpi,
    event_name: "request_info_internal",
    ...buildBasePayload(ctx, "request_info_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
    year_of_entry: yearOfEntry || "na",
    marketing_consent: marketingConsent || "na",
  } as GA4EventAttrType);

  // email_submit (fires alongside request_info_internal)
  GA4DataLayer({
    event: GAConstants.revenue_kpi,
    event_name: "email_submit",
    ...buildBasePayload(ctx, "request_info_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
    year_of_entry: yearOfEntry || "na",
    marketing_consent: marketingConsent || "na",
  } as GA4EventAttrType);
};

/** Fires when Get Prospectus form is submitted successfully (digital or physical) */
export const trackProspectusSubmit = (ctx: LeadFormGAContext, institutionId?: string, institutionName?: string, courseName?: string, yearOfEntry?: string, marketingConsent?: string) => {
  GA4DataLayer({
    event: GAConstants.revenue_kpi,
    event_name: "prospectus_request",
    ...buildBasePayload(ctx, "prospectus_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
    year_of_entry: yearOfEntry || "na",
    marketing_consent: marketingConsent || "na",
  } as GA4EventAttrType);
};

/** Fires when Get Prospectus step 1 is submitted (physical prospectus multi-step) */
export const trackProspectusStep1Submit = (ctx: LeadFormGAContext, institutionId?: string, institutionName?: string, courseName?: string, yearOfEntry?: string, marketingConsent?: string) => {
  GA4DataLayer({
    event: GAConstants.revenue_kpi,
    event_name: "prospectus_request_step1",
    ...buildBasePayload(ctx, "prospectus_start_form"),
    ...(institutionId ? { institution_id: institutionId } : {}),
    ...(institutionName ? { institution_name: institutionName } : {}),
    ...(courseName ? { course_name: courseName } : {}),
    year_of_entry: yearOfEntry || "na",
    marketing_consent: marketingConsent || "na",
  } as GA4EventAttrType);
};
